import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Event : famille d'entités "occurrence datée à capacité partagée".
 *
 * Une occurrence à laquelle plusieurs participants s'inscrivent (cours, atelier,
 * séance, créneau), ponctuelle ou récurrente en série via `recurrenceRule`.
 * Comme pour asset, le socle ne connaît PAS d'entité concrète : le vocabulaire
 * métier ("class", "workshop", "session") est porté par le champ `type`, jamais
 * par le nom de collection.
 *
 * Les inscriptions et les présences ne sont PAS stockées ici : ce sont les
 * familles `registration` et `attendance` qui pointent l'event (via leur propre
 * `eventId`). L'event ne porte que ses références AMONT : la ressource, le lieu,
 * les intervenants et la campagne de rattachement.
 *
 * Convention de patron (réutilisée par toutes les familles, cf. asset) :
 *   - `eventCoreFields`        : les champs MÉTIER seuls (sans champs standard).
 *   - `EventSchema`            : famille complète = champs standard + métier.
 *   - `EventCreateInputSchema` : ce que l'appelant fournit à la création (champs
 *     métier ; les champs standard sont remplis par le SDK). Sert à VALIDER
 *     l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille event (hors champs standard du socle). */
export const eventCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "class", "workshop"). */
  type: z.string().min(1),
  /** Début de l'occurrence, optionnel. */
  startAt: z.date().optional(),
  /** Fin de l'occurrence, optionnel. */
  endAt: z.date().optional(),
  /** Nombre de places partagées (capacité de l'occurrence), optionnel. */
  capacity: z.number().int().optional(),
  /** Prix d'inscription affiché (montant), optionnel. */
  price: z.number().optional(),
  /** Règle de récurrence (chaîne type RRULE) décrivant la série, optionnel. */
  recurrenceRule: z.string().min(1).optional(),
  /** Date limite d'inscription, optionnel. */
  registrationDeadline: z.date().optional(),
  /** Ressource mobilisée (réf. famille resource), optionnel. */
  resourceId: z.string().min(1).optional(),
  /** Lieu de l'occurrence (réf. famille place), optionnel. */
  placeId: z.string().min(1).optional(),
  /** Intervenants affectés (réfs famille worker), optionnel. */
  workerIds: z.array(z.string()).optional(),
  /** Campagne de rattachement (réf. famille campaign), optionnel. */
  campaignId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille event (champs standard + champs métier). */
export const EventSchema = z.object({
  ...baseEntityFields,
  ...eventCoreFields,
});

/** Type d'un event. */
export type Event = z.infer<typeof EventSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const EventCreateInputSchema = z.object({
  ...eventCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un event. */
export type EventCreateInput = z.infer<typeof EventCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const EventUpdateInputSchema = z
  .object({ ...eventCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un event. */
export type EventUpdateInput = z.infer<typeof EventUpdateInputSchema>;
